import type { BuildHubOption } from '~/ui-rocco/hooks/use-build-hubs';

import type { MatchAnswers } from './answers';
import {
    BUDGET_OPTIONS,
    PROJECT_SCOPE_OPTIONS,
    PROJECT_TYPE_OPTIONS,
} from './options';

/**
 * The BuildHub scope a project type implies, read backwards.
 *
 * Mirrors `SCOPE_BY_PROJECT_TYPE` on the server, which turns the question the
 * questionnaire does ask into the scope a project record wants. Going the other
 * way lets an existing project answer that question instead of the reader.
 */
/**
 * A project's scope, as a slug, whichever shape it was stored in.
 *
 * The project creator writes the display label — "Home Renovation" — while
 * briefs and the HDE flow write the slug. Both are in the data, so anything
 * reading a scope has to accept either. Slugifying the label is not enough:
 * "Interior Fit-Out" and "Pool or Spa Installation" do not reduce to
 * `interior-fitout` and `pool-spa`, so the known list is what decides.
 */
export const normaliseScope = (scope: string | null | undefined): string => {
    if (!scope) return '';

    const candidate = scope.trim();
    if (PROJECT_SCOPE_OPTIONS.some((option) => option.value === candidate)) {
        return candidate;
    }

    const byLabel = PROJECT_SCOPE_OPTIONS.find(
        (option) => option.label.toLowerCase() === candidate.toLowerCase(),
    );

    return byLabel?.value ?? '';
};

const PROJECT_TYPE_BY_SCOPE: Record<string, string> = {
    'new-home-build': 'New Build',
    'home-renovation': 'Renovation',
    'extension-addition': 'Extension',
    'interior-fitout': 'Interior Design',
    'landscaping-outdoor': 'Landscaping Project',
    'pool-spa': 'New Build',
    'new-commercial-build': 'Commercial Project',
    'fitout-refurbishment': 'Commercial Project',
    hospitality: 'Commercial Project',
    'retail-showroom': 'Commercial Project',
    'office-workplace': 'Commercial Project',
    'multi-unit-development': 'Commercial Project',
};

/** Upper edge of each budget bracket, in dollars. The last is open-ended. */
const BUDGET_CEILINGS: [number, string][] = [
    [500_000, 'Under $500K'],
    [1_000_000, '$500K – $1M'],
    [2_000_000, '$1M – $2M'],
    [5_000_000, '$2M – $5M'],
];

/**
 * The bracket a project's budget falls in.
 *
 * Read from the minimum rather than the maximum: a project budgeted at
 * $1M–$3M is a $1M–$2M project as far as the question is concerned, because the
 * bracket is about the scale of work being briefed rather than its ceiling.
 */
export const budgetBracketFor = (
    budget: BuildHubOption['Budget'],
): string | undefined => {
    const min = budget?.Min;
    if (min === undefined || min === null) return undefined;

    const bracket = BUDGET_CEILINGS.find(([ceiling]) => min <= ceiling);
    const label = bracket ? bracket[1] : '$5M+';

    // Guard against the two lists drifting apart: a bracket the question does
    // not offer would pre-answer with something unselectable.
    return BUDGET_OPTIONS.some((option) => option.value === label)
        ? label
        : undefined;
};

/**
 * How soon the project says work starts.
 *
 * A start date already behind us reads as the nearest window rather than as an
 * error — a project underway is not "just exploring", and the reader can say
 * otherwise on the step, which is still asked.
 */
export const startTimingFor = (
    startDate: string | null | undefined,
    now: Date = new Date(),
): string | undefined => {
    if (!startDate) return undefined;

    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return undefined;

    const months =
        (start.getFullYear() - now.getFullYear()) * 12 +
        (start.getMonth() - now.getMonth());

    if (months <= 3) return 'Within 1-3 months';
    if (months <= 6) return 'In 3–6 months';
    return '6+ months';
};

/**
 * What a project can answer on the reader's behalf.
 *
 * Only what the project genuinely knows. Style, who you need and what matters
 * most describe this request rather than the project, and are always asked; how
 * soon they want to hear back is about the request too, which is why the
 * timeline step is pre-answered but still shown.
 */
export const answersFromProject = (
    project: BuildHubOption,
    now?: Date,
): Partial<MatchAnswers> => {
    const scope = normaliseScope(project.ProjectScope);
    const projectType = scope ? PROJECT_TYPE_BY_SCOPE[scope] : undefined;

    return {
        budget: budgetBracketFor(project.Budget) ?? '',
        buildHubID: Number(project.ID),
        location: project.LocationData ?? null,
        projectType:
            projectType &&
            PROJECT_TYPE_OPTIONS.some((option) => option.value === projectType)
                ? projectType
                : '',
        projectScope: scope,
        region:
            project.LocationData?.region ||
            project.LocationData?.city ||
            project.Location ||
            '',
        startTiming: startTimingFor(project.StartDate, now) ?? '',
        useProjectDetails: true,
    };
};
